import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../auth/Auth';
import { useModal } from '../hooks/modalContext';
import { PushNotificationSubscribeModal } from '../Modals/PushNotificationSubscribe/PushNotificationSubscribeModal';
import {
  isWebPushEnvironmentReady,
  registerPushServiceWorker,
  syncPushSubscriptionWithBackend,
} from './webPushClient';
import { pushStorage } from './pushNotificationStorage';

const PROMPT_DELAY_MS = 2800;

export interface PushNotificationBootstrapProps {
  hasCompletedIdv?: boolean;
  hasMadeFirstPayment?: boolean;
}

/**
 * Syncs VAPID web push when permission is already granted.
 *
 * Triggers the in-app subscription prompt when ALL of the following are true:
 *  - The user has successfully completed identity verification (IDV).
 *  - Browser notification permission is still 'default' (not yet granted or denied).
 *  - The prompt has not been dismissed in the current browser session.
 *  - No other in-app modal is currently open.
 *
 * The prompt re-appears on every login until the user grants permission.
 * Dismissing the modal ("Not now", backdrop click, or X) suppresses it for the remainder of the session.
 */
export function PushNotificationBootstrap({
  hasCompletedIdv = false,
}: PushNotificationBootstrapProps = {}) {
  const { auth, currentUser } = useAuth();
  const { modal } = useModal();
  const timeoutRef = useRef<number | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const userId = currentUser?.user?.userId;

  const handleClose = () => {
    if (userId != null) {
      pushStorage.setDismissedThisSession(userId);
    }
    setShowPrompt(false);
  };

  useEffect(() => {
    const apiToken = auth?.data?.token;
    if (!apiToken || userId == null) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      if (!isWebPushEnvironmentReady() || cancelled) return;

      if (!('Notification' in window)) return;

      // Browser hard-denied — nothing we can do
      if (Notification.permission === 'denied') {
        return;
      }

      try {
        await registerPushServiceWorker();
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[web push] Service worker registration failed:', err);
        }
        return;
      }

      if (cancelled) return;

      // Already granted — silently sync subscription token and stop
      if (Notification.permission === 'granted') {
        try {
          await syncPushSubscriptionWithBackend(apiToken, userId);
        } catch {
          void 0;
        }
        return;
      }

      if (Notification.permission !== 'default') {
        return;
      }

      // Gate: only prompt once the user has successfully completed IDV
      const userHasCompletedIdv =
        hasCompletedIdv || pushStorage.hasCompletedIdv(userId);
      if (!userHasCompletedIdv) {
        return;
      }

      // Gate: do not re-prompt within the same browser session once dismissed
      if (pushStorage.isDismissedThisSession(userId)) {
        return;
      }

      // Gate: do not overlay an active modal (e.g. payment receipt, IDV)
      if (modal !== null) {
        return;
      }

      timeoutRef.current = window.setTimeout(() => {
        if (!cancelled) {
          setShowPrompt(true);
        }
      }, PROMPT_DELAY_MS);
    };

    void run();

    return () => {
      cancelled = true;
      setShowPrompt(false);
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [auth?.data?.token, userId, hasCompletedIdv, modal]);

  if (!showPrompt) {
    return null;
  }

  return (
    <div
      className='fixed inset-0 z-[200] flex items-center justify-center'
      role='presentation'
      onClick={handleClose}
    >
      <div className='absolute inset-0 bg-[rgba(128,132,147,0.75)] backdrop-blur-sm' />
      <div
        className='relative z-10 flex w-full justify-center px-4'
        role='dialog'
        aria-modal='true'
        onClick={(event) => event.stopPropagation()}
      >
        <PushNotificationSubscribeModal onClose={handleClose} />
      </div>
    </div>
  );
}
